import { useState } from 'react';
import styled from 'styled-components';
import { TeamRef } from '../api/client';
import { flag, flagUrl, teamName } from '../lib/flags';

// Qué mostrar para un TeamRef: equipo real (resuelto/confirmado) o el texto del placeholder.
export function refDisplay(t: TeamRef): { team: string | null; text: string; projected: boolean } {
  const team = t.resolved ?? (t.placeholder ? null : t.name);
  return {
    team,
    text: team ? teamName(team) : t.label ?? t.name,
    projected: t.projected && team !== null,
  };
}

const FlagBox = styled.span<{ $size: number }>`
  flex: 0 0 auto;
  width: ${({ $size }) => $size * 1.4}px;
  height: ${({ $size }) => $size}px;
  border-radius: 4px;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceAlt};
  font-size: ${({ $size }) => $size * 0.8}px;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

// Bandera-imagen con fallback a emoji (si falla la red o no hay código) o /🏳️ para placeholders.
export function Flag({ team, size = 22 }: { team: string | null; size?: number }) {
  const [failed, setFailed] = useState(false);
  // Pedir la bandera al ancho que corresponde al tamaño mostrado (no siempre w160): en el cuadro
  // son 16px → w40 pesa ~1/3 y carga mucho más rápido (hay decenas de banderas por pantalla).
  const url = team ? flagUrl(team, size <= 20 ? 40 : size <= 40 ? 80 : 160) : null;
  if (team && url && !failed) {
    return (
      <FlagBox $size={size}>
        <img src={url} alt={team} loading="lazy" decoding="async" onError={() => setFailed(true)} />
      </FlagBox>
    );
  }
  return <FlagBox $size={size}>{team ? flag(team) : '🏳️'}</FlagBox>;
}

const Wrap = styled.div<{ $align: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-direction: ${({ $align }) => ($align === 'right' ? 'row-reverse' : 'row')};
`;

const Name = styled.span<{ $muted: boolean }>`
  font-weight: 600;
  font-size: 1.05rem;
  color: ${({ theme, $muted }) => ($muted ? theme.colors.textMuted : theme.colors.text)};
  font-style: ${({ $muted }) => ($muted ? 'italic' : 'normal')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Dot = styled.span`
  font-size: 0.6rem;
  color: ${({ theme }) => theme.colors.gold};
  margin-left: 2px;
`;

// Badge completo: bandera + nombre (con marca de "proyectado").
export function TeamBadge({
  team: t,
  align = 'left',
  size = 26,
}: {
  team: TeamRef;
  align?: 'left' | 'right';
  size?: number;
}) {
  const { team, text, projected } = refDisplay(t);
  return (
    <Wrap $align={align}>
      <Flag team={team} size={size} />
      <Name $muted={team === null}>
        {text}
        {projected && <Dot title="Proyectado (el grupo sigue en juego)">●</Dot>}
      </Name>
    </Wrap>
  );
}
